import type { RootState } from '@/app/types/store';
import { layoutApi } from '../api/layoutApi';

// User Context selectors
export const selectUserContext = (state: RootState) => layoutApi.endpoints.getUserContext.select()(state)?.data;
export const selectLayoutUser = (state: RootState) => selectUserContext(state)?.user;
export const selectAvailableProjects = (state: RootState) => selectUserContext(state)?.projects || [];

// Project Data selectors (requires projectId and siteId params)
// These return selector functions that can be used with useSelector
export const selectProjectData = (projectId: number, siteId: number) => (state: RootState) => {
  const selector = layoutApi.endpoints.getProjectData.select({ projectId, siteId });
  return selector(state)?.data;
};

export const selectCurrentProject = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.project;
};

export const selectCurrentSite = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.site;
};

export const selectLayoutMenu = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.menu;
};

export const selectMenuItems = (projectId: number, siteId: number) => (state: RootState) => {
  const menu = selectLayoutMenu(projectId, siteId)(state);
  return menu?.items || [];
};

export const selectMenuBadges = (projectId: number, siteId: number) => (state: RootState) => {
  const menu = selectLayoutMenu(projectId, siteId)(state);
  return menu?.badges;
};

// Context-based selectors from getContext endpoint
export const selectContext = (state: RootState) => layoutApi.endpoints.getContext.select()(state)?.data;
export const selectIsReadOnly = (state: RootState) => selectContext(state)?.readOnly || false;

export const selectBadges = (state: RootState) => layoutApi.endpoints.getBadges.select()(state)?.data;
export const selectInstanceInfo = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.instance;
};

export const selectUserPermissions = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.permissions;
};

export const selectBuildInfo = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.buildInfo;
};

export const selectIsProjectReadOnly = (projectId: number, siteId: number) => (state: RootState) => {
  const data = selectProjectData(projectId, siteId)(state);
  return data?.readOnly || false;
};
